import { FC, MouseEventHandler, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import type { MdiReactIconComponentType } from 'mdi-react';
import AccountCircleIcon from 'mdi-react/AccountCircleIcon';
import BriefcaseCheckIcon from 'mdi-react/BriefcaseCheckIcon';
import CheckAllIcon from 'mdi-react/CheckAllIcon';
import ChevronDownIcon from 'mdi-react/ChevronDownIcon';
import HomeIcon from 'mdi-react/HomeIcon';
import LoginVariantIcon from 'mdi-react/LoginVariantIcon';
import PlusCircleMultipleOutlineIcon from 'mdi-react/PlusCircleMultipleOutlineIcon';
import PowerIcon from 'mdi-react/PowerIcon';
import StarCircleIcon from 'mdi-react/StarCircleIcon';
import CommentAlertIcon from 'mdi-react/CommentAlertIcon';
import SwapHorizontalIcon from 'mdi-react/SwapHorizontalIcon';
import ViewDashboardIcon from 'mdi-react/ViewDashboardIcon';
import TrendingUpIcon from 'mdi-react/TrendingUpIcon';

import Logo from '@z-components/atoms/Logo';
import { auth, currentUserSelector, signOut } from 'modules/users/duck';
import { User } from 'modules/users/types';
import { Link } from 'routes';
import { useClickOutsideElement } from 'src/hooks';
import { getItem, setItem } from 'utils/sessionStorage';
import { isUrlOnPath } from 'utils/url';

import NotificationContainer from '../NotificationContainer';
import UserInfo from '../UserInfo';

import styles from './Header.module.scss';
import { logEvent } from 'utils/analytics';

const SESSION_STORAGE_KEY = 'ngStorage-isClientMode';

interface MenuItem {
  icon?: MdiReactIconComponentType;
  isExternal?: boolean;
  isOpenBlank?: boolean;
  key: string;
  only?: 'desktop' | 'mobile';
  url: string;
}

const HOME: MenuItem = {
  icon: HomeIcon,
  key: 'header.home',
  only: 'mobile',
  url: '/',
};

const ABOUT: MenuItem = {
  icon: HomeIcon,
  key: 'header.about',
  only: 'desktop',
  url: '/about',
};

const PRO: MenuItem = {
  icon: BriefcaseCheckIcon,
  key: 'header.joinAsAPro',
  url: '/pro',
};

const PRO_SUCCESS: MenuItem = {
  icon: TrendingUpIcon,
  isExternal: true,
  isOpenBlank: true,
  key: 'header.proSuccessStories',
  url: 'https://pro.zehitomo.com',
};

// const PRO_SEMINAR: MenuItem = {
//   isExternal: true,
//   key: 'header.proSeminarSignup',
//   url: 'https://pro.zehitomo.com/seminar',
// };

const LOGIN: MenuItem = {
  icon: LoginVariantIcon,
  isExternal: true,
  key: 'header.login',
  url: '/sign-in',
};

const NEW_REQUESTS: MenuItem = {
  icon: PlusCircleMultipleOutlineIcon,
  isExternal: true,
  key: 'header.newRequests',
  url: '/new-requests',
};

const NEW_MATCHES: MenuItem = {
  icon: ViewDashboardIcon,
  isExternal: true,
  key: 'header.messages',
  url: '/dashboard/pro/new-matches',
};

const SERVICES: MenuItem = {
  icon: CheckAllIcon,
  isExternal: true,
  key: 'header.services',
  url: '/services',
};

const PROFILE: MenuItem = {
  icon: AccountCircleIcon,
  isExternal: true,
  key: 'account',
  url: '/account/view-profile',
};

const MISSIONS: MenuItem = {
  icon: StarCircleIcon,
  isExternal: true,
  key: 'missions',
  url: '/missions',
};

const ANNOUNCEMENT: MenuItem = {
  icon: CommentAlertIcon,
  isExternal: true,
  key: 'announcement',
  url: '/announcement',
};

const CLIENT_DASHBOARD: MenuItem = {
  icon: ViewDashboardIcon,
  isExternal: true,
  key: 'dashboard',
  url: '/dashboard/client/my-requests',
};

const CLIENT_EDIT: MenuItem = {
  icon: AccountCircleIcon,
  isExternal: true,
  key: 'account',
  url: '/account/client-edit',
};

const CLIENT_ITEMS = [CLIENT_DASHBOARD, CLIENT_EDIT];
const PRO_ITEMS = [
  NEW_REQUESTS,
  NEW_MATCHES,
  SERVICES,
  PROFILE,
  MISSIONS,
  ANNOUNCEMENT,
  PRO_SUCCESS,
];
interface Props {
  hasMenu?: boolean;
}

const Header: FC<Props> = ({ hasMenu = true }) => {
  const {
    i18n: { language },
    t,
  } = useTranslation();
  const { asPath } = useRouter();
  const dispatch = useDispatch();
  const desktopPopup = useRef<HTMLDivElement>(null);
  const user = useSelector(currentUserSelector) as User | undefined;
  const [isMobileHeaderOpen, setMobileHeaderIsOpen] = useState(false);
  const [isDesktopPopupOpen, setIsDesktopPopupOpen] = useState(false);
  const [isClientMode, setIsClientMode] = useState<boolean>();
  useEffect(() => {
    if (!user) {
      // Note: Could move this somewhere else maybe
      dispatch(auth());
      return;
    }

    const defaultIsClientMode = !user.proProfile;
    try {
      const modeSession = getItem(SESSION_STORAGE_KEY);
      setIsClientMode(modeSession ? modeSession === 'true' : defaultIsClientMode);
    } catch (error) {
      setIsClientMode(defaultIsClientMode);
    }
  }, [dispatch, user]);

  const handleToggleMobileMenu = useCallback<MouseEventHandler>((event) => {
    event.stopPropagation();
    setMobileHeaderIsOpen((value) => {
      const body = document.querySelector('body');
      if (value) body?.classList.remove('no-scroll-mobile');
      else body?.classList.add('no-scroll-mobile');
      return !value;
    });
  }, []);

  const handleModeSwitch = useCallback(() => {
    setItem(SESSION_STORAGE_KEY, isClientMode ? 'false' : 'true');
  }, [isClientMode]);

  const handleSignOut = useCallback(() => {
    dispatch(signOut());
  }, [dispatch]);

  const toggleDesktopMenuPopup = useCallback(() => {
    setIsDesktopPopupOpen((value) => !value);
  }, []);
  useClickOutsideElement(desktopPopup, toggleDesktopMenuPopup, isDesktopPopupOpen);

  const handleGoogleAnalytics = useCallback((menuItemKey) => {
    if (menuItemKey === PRO.key) {
      logEvent('proSignUp', 'clickOnProLP', 'headerMenu');
    }
  }, []);

  const menuItems = useMemo(() => {
    // hide menu on /pro page for non-login user and client
    if (isUrlOnPath(asPath, '/pro') && !user) return [];

    if (!user) {
      return [
        HOME,
        ABOUT,
        PRO,
        // ...(isUrlOnPath(asPath, '/pro') ? [PRO_SUCCESS, PRO_SEMINAR] : []),
        LOGIN,
      ];
    }

    return user.proProfile && !isClientMode ? PRO_ITEMS : CLIENT_ITEMS;
  }, [asPath, user, isClientMode]);

  const hasSomeMenu = hasMenu && menuItems.length > 0;

  const loggedInList = (
    <>
      {user?.proProfile ? (
        <li>
          <a
            className={styles.link}
            onClick={handleModeSwitch}
            href={`${language === 'ja' ? '' : `/${language}`}/dashboard/${
              isClientMode ? 'pro' : 'client'
            }`}
          >
            <SwapHorizontalIcon size={18} />
            <span>{t(isClientMode ? 'header.switchToProMode' : 'header.switchToClientMode')}</span>
          </a>
        </li>
      ) : (
        <li>
          <Link prefetch={false} route="/pro" locale={false}>
            <a className={styles.link}>
              <BriefcaseCheckIcon size={18} />
              <span>{t('header.joinAsAPro')}</span>
            </a>
          </Link>
        </li>
      )}
      <li>
        <button className={styles.link} onClick={handleSignOut}>
          <PowerIcon size={18} />
          <span>{t('header.signOut')}</span>
        </button>
      </li>
    </>
  );

  return (
    <header
      className={classNames(styles.container, { [styles['container-has-menu']]: hasMenu })}
      data-test-id="header"
    >
      {hasSomeMenu ? (
        <button
          aria-label="Toggle Menu"
          className={styles.toggle}
          data-test-id="header-menu-toggle"
          onClick={handleToggleMobileMenu}
        >
          {t('header.menu')}
        </button>
      ) : (
        <div />
      )}
      <Link prefetch={false} route={'/'} locale={false}>
        <a aria-label="To homepage" className={styles.logo} data-test-id="header-link-home">
          <Logo variant="blue" context="header" app="react" />
        </a>
      </Link>
      {hasSomeMenu && (
        <nav
          className={classNames(styles.menu, {
            [styles['menu-auth']]: user,
            [styles['menu-open']]: isMobileHeaderOpen,
          })}
        >
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
          <div className={styles.overlay} onClick={handleToggleMobileMenu}>
            <button className={styles.close} onClick={handleToggleMobileMenu}>
              {t('header.closeMenu')}
            </button>
          </div>
          <div className={styles['content-container']}>
            {user && (
              <div className={styles['user-info']}>
                <UserInfo
                  avatar={user.profile?.avatar}
                  isPro={Boolean(user.proProfile)}
                  // @ts-expect-error Incomplete types
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  privateProfile={user.privateProfile}
                  profile={user.profile}
                  // @ts-expect-error Incomplete types
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  tier={user.currentTier}
                />
              </div>
            )}
            <ul className={styles.list} data-cy={'header-list'}>
              {menuItems.map((item) => (
                <li
                  className={classNames({
                    [styles.desktop]: item.only === 'desktop',
                    [styles.mobile]: item.only === 'mobile',
                  })}
                  key={item.key}
                >
                  {item.isExternal ? (
                    <a
                      className={classNames(styles.link, {
                        [styles.active]: item.url === asPath,
                      })}
                      href={item.url}
                      target={item.isOpenBlank ? '_blank' : '_self'}
                      rel="noopener noreferrer"
                    >
                      {/* @ts-expect-error Icon type */}
                      {Boolean(item.icon) && <item.icon size={18} />}
                      <span>{t(item.key)}</span>
                    </a>
                  ) : (
                    <Link prefetch={false} route={item.url} locale={false}>
                      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                      <a
                        className={classNames(styles.link, {
                          [styles.active]: item.url === asPath,
                        })}
                        onClick={() => {
                          handleGoogleAnalytics(item.key);
                        }}
                      >
                        {/* @ts-expect-error Icon type */}
                        {Boolean(item.icon) && <item.icon size={18} />}
                        <span>{t(item.key)}</span>
                        {item.key === PRO.key && (
                          <span className={styles['menu-item-attention-text-bubble']}>
                            {t('header.joinAsAProAttentionText')}
                          </span>
                        )}
                      </a>
                    </Link>
                  )}
                </li>
              ))}
            </ul>
            {user && (
              <ul className={classNames(styles.list, styles['logged-in-list-mobile'])}>
                {loggedInList}
              </ul>
            )}
          </div>
        </nav>
      )}
      {hasMenu && user && (
        <div className={styles['notification-area']}>
          <NotificationContainer />
          <div
            className={styles['desktop-popup-container']}
            data-test-id="auth-desktop"
            ref={desktopPopup}
          >
            <button
              aria-controls="auth-settings-dropdown"
              aria-expanded={isDesktopPopupOpen}
              aria-haspopup={true}
              className={styles['avatar-button']}
              id="auth-settings-button"
              onClick={toggleDesktopMenuPopup}
            >
              <img
                alt={t('header.settings')}
                src={user.profile?.avatar ?? '/static/images/empty-avatar.svg'}
              />
              <ChevronDownIcon />
            </button>
            <ul
              aria-labelledby="auth-settings-button"
              className={classNames(styles.list, styles['logged-in-list-desktop'], {
                [styles['logged-in-list-desktop-hide']]: !isDesktopPopupOpen,
              })}
              id="auth-settings-dropdown"
            >
              {loggedInList}
            </ul>
          </div>
        </div>
      )}
    </header>
  );
};

export type { Props as HeaderProps };
export default Header;
